import { ref, watch, nextTick } from "vue";
import type { Ref } from "vue";
import type { ListData, ListOptions } from "../types";
import {
  OperationInput,
  MemberUpdateInput,
  MemberListInput,
  MemberCountInput,
  DeleteMemberMutation,
  DeleteMemberMutationVariables,
  DeleteMemberDocument,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getMember, getMemberCount, listMember } from "@/common/graphql/member";
import { displayPagingRange } from "@/common/common";

interface UseMemberActionsParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editPage: Ref<boolean>;
  confirmPage: Ref<boolean>;
  overlay: Ref<boolean>;
  confirmDeleteDialog: Ref<boolean>;
  openConfirmDeletePage: () => void;
  searchConditionName: Ref<string | undefined | null>;
  searchConditionPhone: Ref<string | undefined | null>;
  searchConditionZipCode: Ref<string | undefined | null>;
  searchConditionHouseCard: Ref<string | undefined | null>;
  listOptions: ListOptions;
  listDataArray: ListData[];
  listDataTotalCount: Ref<number>;
  listLoading: Ref<boolean>;
  displayPagingRangeText: Ref<string>;
  selectListCheckbox: Ref<ListData[]>;
}

/**
 * 画面の操作イベントに関するハンドラをまとめて提供する composable。
 *
 * @returns 画面イベントから呼び出す操作ハンドラ群
 */
export const useMemberActions = (params: UseMemberActionsParams) => {
  const {
    errdialog,
    errMessage,
    editPage,
    confirmPage,
    overlay,
    confirmDeleteDialog,
    openConfirmDeletePage,
    searchConditionName,
    searchConditionPhone,
    searchConditionZipCode,
    searchConditionHouseCard,
    listOptions,
    listDataArray,
    listDataTotalCount,
    listLoading,
    displayPagingRangeText,
    selectListCheckbox,
  } = params;

  // 編集対象 会員
  const editMember = ref<MemberUpdateInput | null>(null);

  // 検索ボタンからの呼び出し時にwatchをスキップするフラグ
  const isSearchButtonClick = ref(false);

  /**
   * 絞り込み検索
   */
  const searchQuery = async (): Promise<void> => {
    // 会員一覧 input [limit 取得件数 offset:取得開始位置]
    const listInput: MemberListInput = {
      kana:
        searchConditionName.value !== undefined &&
        searchConditionName.value !== null &&
        searchConditionName.value !== ""
          ? searchConditionName.value
          : undefined,
      phone:
        searchConditionPhone.value !== undefined &&
        searchConditionPhone.value !== null &&
        searchConditionPhone.value !== ""
          ? searchConditionPhone.value
          : undefined,
      zip_code:
        searchConditionZipCode.value !== undefined &&
        searchConditionZipCode.value !== null &&
        searchConditionZipCode.value !== ""
          ? searchConditionZipCode.value
          : undefined,
      house_card:
        searchConditionHouseCard.value !== undefined &&
        searchConditionHouseCard.value !== null &&
        searchConditionHouseCard.value !== ""
          ? searchConditionHouseCard.value
          : undefined,
      chain_code: localStorageGetChainCode(),
      shop_code: localStorageGetShopCode(),
      customer_code: localStorageGetCustomerCode(),
      limit: listOptions.itemsPerPage,
      offset: (listOptions.page - 1) * listOptions.itemsPerPage,
    };

    // 会員作成 logInput
    const operationInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 会員件数 input
    const countInput: MemberCountInput = {
      chain_code: listInput.chain_code,
      customer_code: listInput.customer_code,
      shop_code: listInput.shop_code,
      kana: listInput.kana,
      phone: listInput.phone,
      zip_code: listInput.zip_code,
      house_card: listInput.house_card,
    };

    // 会員一覧取得
    const list = await listMember(listInput, operationInput);
    // 会員一覧のデータを作成
    listDataArray.length = 0;
    list!.forEach((value) => {
      if (value !== undefined && value !== null) {
        listDataArray.push({
          memberId: value.member_id,
          name: value.name1 + value.name2,
          name_kana: value.name_kana1 + value.name_kana2,
          zip_code: value.zip_code,
          address:
            value.address1 +
            value.address2 +
            (value.address3 ? value.address3 : ""),
          phone: value.phone,
          house_card:
            value.house_card !== undefined && value.house_card !== null
              ? value.house_card
              : "",
        });
      }
    });

    // 絞込み後の全データ数 更新
    listDataTotalCount.value = await getMemberCount(countInput);

    // ページングの表示
    displayPagingRangeText.value = displayPagingRange(
      listOptions.page - 1,
      listOptions.itemsPerPage,
      listDataTotalCount.value
    );

    // 編集対象 会員
    editMember.value = null;
  };

  /**
   * 絞り込み検索 @Click
   */
  const onClickSearch = async (): Promise<void> => {
    // 検索ボタンをクリックした場合、ページを1に戻す（既に1ページ目の場合はスキップ）
    if (listOptions.page !== 1) {
      isSearchButtonClick.value = true;
      listOptions.page = 1;
    }

    //検索する
    await searchQuery();
  };

  /**
   * listOptions監視
   */
  watch(
    () => ({ ...listOptions }),
    async () => {
      // 検索ボタンからの呼び出し時はスキップ（onClickSearchで直接処理）
      if (isSearchButtonClick.value) {
        isSearchButtonClick.value = false;
        return;
      }
      listLoading.value = true;
      await searchQuery();
      listLoading.value = false;
    },
    { deep: true }
  );

  /**
   * 会員編集アイコン @Click
   */
  const onClickEdit = async (memberId: string): Promise<void> => {
    // 会員取得
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 編集対象 会員
    editMember.value = (await getMember(
      memberId,
      logInput
    )) as unknown as MemberUpdateInput;

    //編集ページ表示
    editPage.value = true;
  };

  /**
   * 一括削除 @Click
   */
  const onClickCheckDelete = async (): Promise<void> => {
    if (selectListCheckbox.value.length === 0) {
      return;
    }
    confirmDeleteDialog.value = true;
  };

  /**
   * 一括削除 実行
   */
  const onClickCheckDeleteComplete = async (): Promise<void> => {
    overlay.value = true;

    //1件ずつ削除する
    let result = false;
    let arrayError: string[] = [];
    for (let val of selectListCheckbox.value) {
      if (await deleteExecute(val.memberId)) {
        result = true;
      } else {
        arrayError.push(val.memberId);
      }
    }

    //画面を閉じる
    confirmDeleteDialog.value = false;
    overlay.value = false;

    //画面遷移
    if (result) {
      await nextTick(async () => {
        //削除
        selectListCheckbox.value = [];
        // 絞り込み検索
        await searchQuery();
      });
    } else {
      errMessage.value = "受付番号 " + arrayError.join(",") + "の削除に失敗しました。";
      errdialog.value = true;
    }
  };

  /**
   * 会員削除アイコン @Click
   */
  const onClickDelete = async (listData: ListData): Promise<void> => {
    //データ取得をする
    await onClickEdit(listData.memberId);

    // 確認ページを表示
    openConfirmDeletePage();
  };

  /**
   * 会員削除確認OK @Click
   */
  const onClickConfirmDeleteOk = async (): Promise<void> => {
    //編集ページ表示 OFF
    editPage.value = false;

    //確認ページ表示 OFF
    confirmPage.value = false;

    if (await deleteExecute(editMember.value!.member_id)) {
      await nextTick(async () => {
        // 絞り込み検索
        await searchQuery();
      });
    } else {
      errdialog.value = true;
      errMessage.value = "削除処理に失敗しました。";
    }
  };

  /**
   * 削除処理GraphQL実行(1件ずつ)
   */
  const deleteExecute = async (memberId: string): Promise<boolean> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 会員削除 logInput
    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 会員削除
    try {
      await apolloClient.mutate<DeleteMemberMutation, DeleteMemberMutationVariables>({
        mutation: DeleteMemberDocument,
        variables: {
          logInput: logInput,
          member_id: memberId,
        },
      });
      return true;
    } catch (err: unknown) {
      console.error("err=", err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error
          ? "会員の削除に失敗しました。"
          : "会員の削除中に予期せぬエラーが発生しました。";
      return false;
    }
  };

  return {
    editMember,
    searchQuery,
    onClickSearch,
    onClickEdit,
    onClickCheckDelete,
    onClickCheckDeleteComplete,
    onClickDelete,
    onClickConfirmDeleteOk,
  };
};
